import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchCalculatorStatus } from '../api';
import { CalculatorStatus } from '../types';
import { useAuth } from '../context/AuthContext';

export interface UseCalculatorStatusOptions {
  enabled?: boolean;
  activePollIntervalMs?: number; // When running (default 1500ms)
  idlePollIntervalMs?: number;   // When idle/completed (default 8000ms)
  onCompleted?: () => void;
}

export interface UseCalculatorStatusResult {
  status: CalculatorStatus | null;
  loading: boolean;
  error: string | null;
  isRunning: boolean;
  refetch: () => Promise<CalculatorStatus | null>;
  setOptimisticRunning: (phase?: string) => void;
  startFastPolling: (durationMs?: number) => void;
}

export const useCalculatorStatus = (options: UseCalculatorStatusOptions = {}): UseCalculatorStatusResult => {
  const { isAdmin, isAuthenticated } = useAuth();
  const {
    enabled = true,
    activePollIntervalMs = 1500,
    idlePollIntervalMs = 8000,
    onCompleted,
  } = options;

  const [status, setStatus] = useState<CalculatorStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const prevStatusRef = useRef<string | undefined>(undefined);
  const lastCompletedRunIdRef = useRef<string | undefined>(undefined);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const fastPollingUntilRef = useRef<number>(0);
  const triggeredAtRef = useRef<number>(0);

  const canFetch = enabled && isAuthenticated && isAdmin;

  const loadStatus = useCallback(async (isManual = false) => {
    if (!canFetch) {
      return null;
    }

    if (isManual) {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await fetchCalculatorStatus({ fresh: true });

      // Stale status protection during optimistic trigger window
      if (triggeredAtRef.current > 0 && data) {
        const candidateTime = new Date(data.completedAt || data.lastHeartbeat || data.startedAt || 0).getTime();
        // A run is only considered the NEW run if its timestamp is at or after triggeredAt (with 3s clock skew tolerance)
        // AND it has reported an active status
        const isFreshNewRun =
          candidateTime >= (triggeredAtRef.current - 3000) &&
          (data.status === 'Running' || data.status === 'Completed' || data.status === 'Failed');

        if (!isFreshNewRun && Date.now() < fastPollingUntilRef.current) {
          // Still waiting for the new worker to write its first status update.
          // Keep displaying the optimistic "Running" status without flickering to idle!
          return null;
        }

        // We now have the confirmed new run or timeout
        triggeredAtRef.current = 0;
      }

      if (data) {
        setStatus(data);

        const currentStatus = data?.status;
        const currentRunId = data.runId || data.completedAt || '';

        const isNewCompletion =
          currentStatus === 'Completed' &&
          (
            prevStatusRef.current === 'Running' ||
            (triggeredAtRef.current > 0 && currentRunId !== lastCompletedRunIdRef.current) ||
            (lastCompletedRunIdRef.current !== undefined && currentRunId !== '' && currentRunId !== lastCompletedRunIdRef.current)
          );

        if (isNewCompletion) {
          lastCompletedRunIdRef.current = currentRunId;
          triggeredAtRef.current = 0;
          if (onCompletedRef.current) {
            onCompletedRef.current();
          }
        } else if (currentStatus === 'Completed' && !lastCompletedRunIdRef.current) {
          lastCompletedRunIdRef.current = currentRunId;
        }

        prevStatusRef.current = currentStatus;
      }

      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load calculator status';
      setError(msg);
      return null;
    } finally {
      if (isManual) {
        setLoading(false);
      }
    }
  }, [canFetch]);

  const setOptimisticRunning = useCallback((phase?: string) => {
    const now = Date.now();
    triggeredAtRef.current = now;
    fastPollingUntilRef.current = now + 240000;
    prevStatusRef.current = 'Running';

    setStatus((prev) => ({
      jobName: 'calculator',
      status: 'Running',
      runId: `optimistic-${now}`,
      startedAt: new Date(now).toISOString(),
      completedAt: undefined,
      lastHeartbeat: new Date(now).toISOString(),
      runDate: prev?.runDate || new Date().toISOString().slice(0, 10),
      totalStocks: prev?.totalStocks || 0,
      processedStocks: 0,
      percentComplete: 0,
      durationSeconds: 0,
      processingRate: 0,
      phase: phase || 'Job queued. Starting serverless worker...',
      errorMessage: undefined,
    }));

    // Poll after a short initial delay to catch instant status from HTTP trigger
    setTimeout(() => {
      void loadStatus(false);
    }, 1200);
  }, [loadStatus]);

  const startFastPolling = useCallback((durationMs = 240000) => {
    fastPollingUntilRef.current = Date.now() + durationMs;
    void loadStatus(false);
  }, [loadStatus]);

  useEffect(() => {
    if (!canFetch) {
      setStatus(null);
      return;
    }

    let isMounted = true;
    let timerId: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      const isFastPolling = Date.now() < fastPollingUntilRef.current;
      const isCurrentlyRunning = status?.status === 'Running';
      const shouldSkipTick = document.hidden && !isCurrentlyRunning && !isFastPolling;

      if (!shouldSkipTick) {
        await loadStatus();
      }

      if (!isMounted) return;

      const nextIsFast = Date.now() < fastPollingUntilRef.current;
      const nextIsRunning = status?.status === 'Running';
      const nextDelay = (nextIsRunning || nextIsFast) ? activePollIntervalMs : idlePollIntervalMs;

      timerId = setTimeout(poll, nextDelay);
    };

    // Initial load
    void loadStatus();

    // Schedule next poll
    const initialDelay = (status?.status === 'Running' || Date.now() < fastPollingUntilRef.current)
      ? activePollIntervalMs
      : idlePollIntervalMs;
    timerId = setTimeout(poll, initialDelay);

    const handleVisibilityChange = () => {
      if (!document.hidden && canFetch && isMounted) {
        void loadStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      if (timerId) clearTimeout(timerId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [canFetch, loadStatus, status?.status, activePollIntervalMs, idlePollIntervalMs]);

  // Consider running only if status is Running and heartbeat is fresh (< 5 minutes old)
  const isHeartbeatFresh =
    !status?.lastHeartbeat ||
    (Date.now() - new Date(status.lastHeartbeat).getTime()) < 300000;
  const isRunning = status?.status === 'Running' && isHeartbeatFresh;

  return {
    status,
    loading,
    error,
    isRunning,
    refetch: () => loadStatus(true),
    setOptimisticRunning,
    startFastPolling,
  };
};
